import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { urgencyColor, urgencyLabel } from '../urgency';
import { ArrowLeft, Flame, Gauge, RotateCcw, Trash2 } from 'lucide-react';
import { jobsApi } from '../../../shared/services/api';
import { useRefresh } from '../../../shared/contexts/RefreshContext';
import { useAuth } from '../../auth';
import { canCreateJob, canWorkJob } from '../../../shared/utils/permissions';
import Pagination, { pageSlice } from '../../../shared/components/Pagination';

/** "TL tl · Manager A" — where the recruiter a job is assigned to sits. */
const teamLine = (p) => [p.team_lead && `TL ${p.team_lead.name}`, p.manager && `Manager ${p.manager.name}`]
  .filter(Boolean).join(' · ');

function Person({ person, withTeam, empty = 'Unassigned' }) {
  if (!person) return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{empty}</span>;
  const team = withTeam ? teamLine(person) : '';
  return (
    <div style={{ minWidth: 150 }}>
      <div style={{ fontWeight: 600, color: 'var(--text-heading)' }}>{person.name}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{person.email}</div>
      {team && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{team}</div>}
    </div>
  );
}

export default function JobsList() {
  const { refreshKey } = useRefresh();
  const { user } = useAuth();
  const mayCreate = canCreateJob(user);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  // Anyone overseeing recruiters needs to know whose job is whose; a recruiter
  // only ever sees jobs assigned to them, so for them only "Created by" says
  // anything (a team lead may have posted it for them).
  const showPoster = user?.role !== 'recruiter';
  const [posterFilter, setPosterFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    jobsApi.list().then(setJobs).catch(() => setJobs([])).finally(() => setLoading(false));
  }, [refreshKey]); // eslint-disable-line

  const handleClone = async (id, e) => {
    e.preventDefault();
    const cloned = await jobsApi.clone(id);
    setJobs(prev => [cloned, ...prev]);
  };

  const handleDelete = async (id, e) => {
    e.preventDefault();
    if (!window.confirm('Move this job to deleted?')) return;
    await jobsApi.delete(id);
    setJobs(prev => prev.map(j => j.id === id ? { ...j, status: 'Deleted' } : j));
  };

  const handleRestore = async (id, e) => {
    e.preventDefault();
    await jobsApi.restore(id);
    setJobs(prev => prev.map(j => j.id === id ? { ...j, status: 'Draft' } : j));
  };

  const handleStatusChange = async (id, newStatus, e) => {
    e.stopPropagation();
    await jobsApi.update(id, { status: newStatus });
    setJobs(prev => prev.map(j => j.id === id ? { ...j, status: newStatus } : j));
  };

  const statusBadge = (s) => {
    const map = {
      Active: 'badge-green',
      Draft: 'badge-gray',
      Paused: 'badge-yellow',
      Closed: 'badge-red',
      Deleted: 'badge-red',
    };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
  };

  const urgencyBadge = (u) => {
    const cfg = {
      label: urgencyLabel(u),
      color: urgencyColor(u),
      Icon: u === 'High' ? Flame : Gauge,
    };
    return (
      <span style={{
        fontSize: 10, fontWeight: 600, color: cfg.color,
        background: `${cfg.color}18`, padding: '2px 6px',
        borderRadius: 6, whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        <cfg.Icon size={10} strokeWidth={2.4} />
        {cfg.label}
      </span>
    );
  };

  useEffect(() => { setPage(1); }, [posterFilter, showDeleted]);

  const activeJobs = jobs.filter(j => j.status !== 'Deleted');
  const deletedJobs = jobs.filter(j => j.status === 'Deleted');
  const byPoster = (j) => !posterFilter
    || (posterFilter === 'unassigned' ? !j.assigned_to : j.assigned_to?.id === posterFilter);
  const displayed = (showDeleted ? deletedJobs : activeJobs).filter(byPoster);
  // Ten to a page; the effect above sends a changed filter back to the first.
  const paged = pageSlice(displayed, page);

  // Filter choices grouped by manager, so the admin can pick "whose team".
  const posters = Object.values(Object.fromEntries(
    jobs.filter(j => j.assigned_to).map(j => [j.assigned_to.id, j.assigned_to]),
  ));
  const posterGroups = {};
  for (const p of posters) {
    const key = p.manager ? `Manager ${p.manager.name}'s team` : 'Not under a manager';
    (posterGroups[key] = posterGroups[key] || []).push(p);
  }
  const hasUnassigned = jobs.some(j => !j.assigned_to);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Job Posts</div>
          <div className="page-subtitle">
            {activeJobs.length} active
            {deletedJobs.length > 0 && ` · ${deletedJobs.length} deleted`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {showPoster && (posters.length > 0 || hasUnassigned) && (
            <select
              className="form-select"
              value={posterFilter}
              onChange={e => setPosterFilter(e.target.value)}
              aria-label="Filter by the recruiter a job is assigned to"
              style={{ width: 260 }}
            >
              <option value="">Assigned to: everyone</option>
              {Object.keys(posterGroups).sort().map(group => (
                <optgroup key={group} label={group}>
                  {posterGroups[group].sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
                  ))}
                </optgroup>
              ))}
              {hasUnassigned && <option value="unassigned">Unassigned</option>}
            </select>
          )}
          {deletedJobs.length > 0 && (
            <button
              className={`btn ${showDeleted ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setShowDeleted(s => !s)}
              style={{ fontSize: 12 }}
            >
              {showDeleted
                ? <><ArrowLeft size={12} strokeWidth={2} /> Active Jobs</>
                : <><Trash2 size={12} strokeWidth={2} /> Deleted ({deletedJobs.length})</>}
            </button>
          )}
          {!showDeleted && mayCreate && (
            <Link to="/jobs/new" className="btn btn-primary">+ New Job Post</Link>
          )}
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ whiteSpace: 'nowrap' }}>Job code</th>
              <th>Job Title</th>
              <th>Created by</th>
              {showPoster && <th>Assigned to</th>}
              <th>Type</th>
              <th>Urgency</th>
              <th>Location</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 ? (
              <tr>
                <td colSpan={showPoster ? 10 : 9} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  {posterFilter ? 'No jobs assigned to this person.' : showDeleted ? 'No deleted jobs.' : 'No jobs yet. Create your first job post.'}
                </td>
              </tr>
            ) : paged.shown.map(job => {
              const isDeleted = job.status === 'Deleted';
              return (
                <tr key={job.id} style={{ opacity: isDeleted ? 0.6 : 1 }}>
                  <td style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {job.job_code || '—'}
                  </td>
                  <td>
                    {isDeleted
                      ? <span style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'line-through' }}>{job.title}</span>
                      : <Link to={`/jobs/${job.id}`} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>{job.title}</Link>
                    }
                  </td>
                  <td><Person person={job.created_by} empty="—" /></td>
                  {showPoster && <td><Person person={job.assigned_to} withTeam /></td>}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className="badge badge-blue" style={{ whiteSpace: 'nowrap' }}>{job.employment_type}</span>
                  </td>
                  <td>{urgencyBadge(job.urgency || 'Medium')}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{[job.city, job.country].filter(Boolean).join(', ') || '—'}</td>
                  <td>
                    {isDeleted || !canWorkJob(user, job) ? statusBadge(job.status) : (
                      <select
                        value={job.status}
                        onChange={e => handleStatusChange(job.id, e.target.value, e)}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 6px',
                          borderRadius: 6, border: '1px solid var(--border)',
                          background: 'var(--bg-surface)', color: 'var(--text-primary)',
                          cursor: 'pointer', outline: 'none',
                        }}
                      >
                        <option value="Active">Active</option>
                        <option value="Draft">Draft</option>
                        <option value="Paused">Paused</option>
                        <option value="Closed">Closed</option>
                      </select>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {new Date(job.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!canWorkJob(user, job) ? (
                        <Link to={`/jobs/${job.id}`} className="btn btn-ghost btn-sm">View</Link>
                      ) : isDeleted ? (
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--green)', borderColor: 'var(--green)' }} onClick={e => handleRestore(job.id, e)}>
                          <RotateCcw size={12} strokeWidth={2} /> Restore
                        </button>
                      ) : (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={e => handleClone(job.id, e)}>Clone</button>
                          <button className="btn btn-danger btn-sm" onClick={e => handleDelete(job.id, e)}>Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Pagination {...paged} noun="jobs" onChange={setPage} />
      </div>
    </div>
  );
}
